import React from 'react';
import {
  Avatar,
  createTheme,
  Box,
  Card,
  Button,
  TextField,
  MenuItem,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Client from '../Client';
import {
  locationsList,
  salesCategoryMixMtd
} from './simpleBiReportsApi';

let theme = createTheme();

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const periodTypes = [
  { value: 'today', name: 'Today' },
  { value: 'yesterday', name: 'Yesterday' },
  { value: 'mtd', name: 'This Month' },
  { value: 'last_month', name: 'Last Month' }
];

const deliveryTypes = [
  { iref: 2, name: 'All' },
  { iref: 0, name: 'Restaurant' },
  { iref: 1, name: 'Delivery' },
  { iref: 3, name: 'App Clients' }
];

const objectToCsv = function (data) {
  const csvRows = [];

  if (!data || !data.length) {
    return "No data";
  }

  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(','));

  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header];
      return `"${String(val).replace(/"/g, '""')}"`;
    });

    csvRows.push(values.join(','));
  }

  return csvRows.join('\n');
};

class SalesCategoryMix extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),

      page: 0,
      rowsPerPage: 10,

      period: 'mtd',
      periodName: 'This Month',

      location: 0,
      locationName: { name: "All Locations" },
      locations: [],

      delivery: 2,
      deliveryName: "All",

      categoryMix: {
        result: [],
        totalValue: 0,
        totalQuantity: 0,
        periodStart: "",
        periodEnd: ""
      },

      filterData: {}
    };

    this.getData = this.getData.bind(this);
    this.doSync = this.doSync.bind(this);
    this.selectLocation = this.selectLocation.bind(this);
    this.selectDelivery = this.selectDelivery.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.selectPeriod = this.selectPeriod.bind(this);
  }

  componentDidMount() {
    this.getData();
  }

  selectPeriod = (event) => {
    const value = event.target.value;
    const periodName = periodTypes.find(o => o.value === value)?.name || "This Month";

    this.setState(
      {
        period: value,
        periodName
      },
      function () {
        this.doSync();
      }
    );
  }

  getData() {
    const queryData = {
      location: this.state.location,
      delivery: this.state.delivery,
      period: this.state.period
    };

    const data = salesCategoryMixMtd(this.state.client, queryData);

    if (!data || !Array.isArray(data?.result)) {
      console.error("Sales Category Mix API unavailable", {
        endpoint: "salesCategoryMixMtd",
        payload: queryData,
        response: data
      });

      this.setState({
        categoryMix: {
          result: [],
          totalValue: 0,
          totalQuantity: 0,
          periodStart: "",
          periodEnd: ""
        },
        filterData: queryData,
        page: 0
      });

      return;
    }

    this.setState({
      categoryMix: data,
      filterData: queryData,
      page: 0
    });

    let locations = locationsList(this.state.client);

    if (locations && Array.isArray(locations.locations)) {
      this.setState({ locations: locations.locations });
    }
  }

  doSync() {
    this.getData();
    this.setState({ page: 0 });
  }

  selectLocation = (event) => {
    const value = event.target.value;

    const locationObj = this.state.locations.find(
      (o) => String(o.iref) === String(value)
    );

    this.setState(
      {
        location: value,
        locationName: locationObj || { name: "All Locations" }
      },
      function () {
        this.doSync();
      }
    );
  }

  selectDelivery = (event) => {
    const value = Number(event.target.value);
    const deliveryName = deliveryTypes.find(o => o.iref === value)?.name || "All";

    this.setState(
      {
        delivery: value,
        deliveryName
      },
      function () {
        this.doSync();
      }
    );
  }

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({
      rowsPerPage: +event.target.value,
      page: 0
    });
  }

  onDownload = () => {
    const rows = this.state.categoryMix?.result || [];
    const locName = this.state.locationName?.name || "All Locations";
    const periodName = this.state.periodName || "Month";

    const csvRows = rows.map((row) => ({
      Category: row.categoryName,
      Quantity: row.quantity,
      Sales: row.value,
      Percent: row.percent
    }));

    const csvHeader = `Sales Category Mix
Period: ${periodName}
Period Start: ${this.state.categoryMix.periodStart || ""}
Period End: ${this.state.categoryMix.periodEnd || ""}
Location: ${locName}
Delivery: ${this.state.deliveryName}
Total Sales: ${this.state.categoryMix.totalValue || 0}
Total Quantity: ${this.state.categoryMix.totalQuantity || 0}`;

    const csvBody = csvRows.length ? objectToCsv(csvRows) : "No data";
    const csv = "\uFEFF" + csvHeader + "\n" + csvBody;

    const link = document.createElement("a");
    link.download = `categoryMix_${locName}_${this.state.period}.csv`;
    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  };

  render() {
    let locations = [{ iref: 0, name: "All Locations" }];

    if (this.state.locations) {
      Object.keys(this.state.locations).forEach(key => {
        let fData = this.state.locations[key];

        if (fData && fData.iref !== undefined) {
          let myData = {
            iref: fData.iref,
            name: fData.name
          };

          locations.push(myData);
        }
      });
    }

    let categoryRows = [];

    const src = this.state.categoryMix?.result;

    if (Array.isArray(src)) {
      src.forEach((item, index) => {
        categoryRows.push({
          id: item.category || index + 1,
          categoryName: item.categoryName || "No category",
          quantity: item.quantity || 0,
          value: item.value || 0,
          percent: item.percent || 0
        });
      });
    }

    const pagedRows = categoryRows.slice(
      this.state.page * this.state.rowsPerPage,
      this.state.page * this.state.rowsPerPage + this.state.rowsPerPage
    );

    const totalValue = this.state.categoryMix?.totalValue || 0;
    const totalQuantity = this.state.categoryMix?.totalQuantity || 0;

    return (
      <Card
        sx={{
          borderRadius: "14px",
          overflow: "hidden",
          height: {
            xs: "calc(100vh - 92px)",
            md: "calc(100vh - 104px)"
          },
          minHeight: {
            xs: 500,
            sm: 540
          },
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Filters */}
        <Box
          sx={{
            px: {
              xs: 1,
              sm: 1.5
            },
            py: {
              xs: 0.75,
              sm: 1
            },
            display: "grid",
            gridTemplateColumns: {
              xs: "1.25fr 0.85fr",
              sm: "180px 130px 120px auto"
            },
            gap: {
              xs: 1,
              sm: 1.25
            },
            alignItems: "end",
            borderBottom: "1px solid rgba(0,0,0,0.08)"
          }}
        >
          <TextField
            id="standard-select-location"
            select
            label="Location"
            value={this.state.location}
            variant="standard"
            size="small"
            sx={this.filterSx()}
            onChange={this.selectLocation}
          >
            {locations.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            id="standard-select-period"
            select
            label="Period"
            value={this.state.period}
            variant="standard"
            size="small"
            sx={this.filterSx()}
            onChange={this.selectPeriod}
          >
            {periodTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            id="standard-select-delivery"
            select
            label="Delivery"
            value={this.state.delivery}
            variant="standard"
            size="small"
            sx={{
              ...this.filterSx(),
              gridColumn: {
                xs: "1 / 2",
                sm: "auto"
              }
            }}
            onChange={this.selectDelivery}
          >
            {deliveryTypes.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          <Button
            color="inherit"
            size="small"
            onClick={this.onDownload}
            variant="contained"
            disabled={!categoryRows.length}
            sx={{
              maxWidth: '38px',
              minWidth: '38px',
              maxHeight: '38px',
              minHeight: '38px',
              display: {
                xs: 'none',
                sm: 'none',
                md: 'inline-flex'
              },
              justifySelf: "end"
            }}
          >
            <Avatar sx={{ width: 25, height: 25, bgcolor: theme.palette.success.light }}>
              <FileDownloadIcon fontSize="small" />
            </Avatar>
          </Button>
        </Box>

        {/* Summary */}
        <Box
          sx={{
            px: {
              xs: 1,
              sm: 1.5
            },
            py: {
              xs: 0.75,
              sm: 1
            },
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(4, 1fr)"
            },
            gap: {
              xs: 1,
              sm: 1.25
            },
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            backgroundColor: "#f6f4ef"
          }}
        >
          <Box>
            <Typography sx={this.summaryLabelSx()}>
              Total Sales
            </Typography>
            <Typography sx={this.summaryValueSx()}>
              {thousandsSeparator(parseInt(totalValue || 0))}
            </Typography>
          </Box>

          <Box>
            <Typography sx={this.summaryLabelSx()}>
              Quantity
            </Typography>
            <Typography sx={this.summaryValueSx()}>
              {thousandsSeparator(parseInt(totalQuantity || 0))}
            </Typography>
          </Box>

          <Box>
            <Typography sx={this.summaryLabelSx()}>
              Categories
            </Typography>
            <Typography sx={this.summaryValueSx()}>
              {categoryRows.length}
            </Typography>
          </Box>

          <Box>
            <Typography sx={this.summaryLabelSx()}>
              Period
            </Typography>
            <Typography
              sx={this.summaryValueSx()}
              title={
                this.state.categoryMix?.periodStart && this.state.categoryMix?.periodEnd
                  ? `${this.state.categoryMix.periodStart} → ${this.state.categoryMix.periodEnd}`
                  : this.state.periodName
              }
            >
              {this.state.periodName}
            </Typography>
          </Box>
        </Box>

        {/* Table */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0
          }}
        >
          <TableContainer
            sx={{
              height: "100%",
              overflow: "auto"
            }}
          >
            <Table stickyHeader aria-label="category mix table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={this.headerCellSx("left")}>
                    <b>Category</b>
                  </TableCell>
                  <TableCell sx={this.headerCellSx("right", 78)}>
                    <b>Sales</b>
                  </TableCell>
                  <TableCell sx={this.headerCellSx("right", 48)}>
                    <b>%</b>
                  </TableCell>
                  <TableCell sx={this.headerCellSx("right", 64)}>
                    <b>Qty</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pagedRows.map((row) => {
                  return (
                    <TableRow
                      hover
                      key={row.id}
                      tabIndex={-1}
                    >
                      <TableCell sx={this.bodyCellSx("left", false)}>
                        {row.categoryName}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx("right", false, 90)}>
                        {thousandsSeparator(parseInt(row.value || 0))}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx("right", false, 64)}>
                        {Number(row.percent || 0).toFixed(1)}%
                      </TableCell>
                      <TableCell sx={this.bodyCellSx("right", false, 80)}>
                        {thousandsSeparator(parseInt(row.quantity || 0))}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!pagedRows.length && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{
                        py: 2,
                        fontSize: {
                          xs: "13.5px",
                          sm: "14px"
                        }
                      }}
                    >
                      No data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, { value: categoryRows.length, label: 'All' }]}
          labelRowsPerPage="Rows:"
          component="div"
          count={categoryRows.length}
          rowsPerPage={this.state.rowsPerPage}
          page={this.state.page}
          onPageChange={this.handleChangePage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid rgba(0,0,0,0.08)",
            ".MuiTablePagination-toolbar": {
              minHeight: {
                xs: 46,
                sm: 52
              },
              px: {
                xs: 1,
                sm: 2
              }
            },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
              fontSize: {
                xs: "13px",
                sm: "13px"
              }
            },
            ".MuiTablePagination-actions button": {
              padding: {
                xs: "4px",
                sm: "8px"
              }
            }
          }}
        />
      </Card>
    );
  }

  filterSx() {
    return {
      width: "100%",
      minWidth: 0,
      "& .MuiInputBase-input": {
        fontSize: {
          xs: "14px",
          sm: "14px"
        }
      },
      "& .MuiInputLabel-root": {
        fontSize: {
          xs: "13px",
          sm: "13px"
        }
      }
    };
  }

  summaryLabelSx() {
    return {
      color: "#1a237e",
      fontSize: {
        xs: "11px",
        sm: "12px"
      },
      fontWeight: 800,
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      lineHeight: 1.2
    };
  }

  summaryValueSx() {
    return {
      color: "#1e2833",
      fontSize: {
        xs: "15px",
        sm: "17px",
        md: "18px"
      },
      fontWeight: 800,
      lineHeight: 1.25,
      mt: 0.25
    };
  }

  headerCellSx(align, minWidth = null) {
    return {
      padding: {
        xs: "8px 4px",
        sm: "7px"
      },
      textAlign: align,
      whiteSpace: "nowrap",
      minWidth: minWidth,
      fontSize: {
        xs: "13px",
        sm: "13px",
        md: "14px"
      },
      backgroundColor: "#f6f4ef"
    };
  }

  bodyCellSx(align, strong = false, minWidth = null) {
    return {
      padding: {
        xs: "8px 4px",
        sm: "7px"
      },
      textAlign: align,
      whiteSpace: align === "left" ? "normal" : "nowrap",
      minWidth: minWidth,
      fontSize: {
        xs: "13.5px",
        sm: "13px",
        md: "14px"
      },
      fontWeight: strong ? 600 : 400,
      lineHeight: 1.35
    };
  }
}

export default SalesCategoryMix;
